import type { CSSProperties } from "react";
import type { SiteConfig } from "../config/SiteConfigContext";

/**
 * Colori del tema (modificabili dalla dashboard) come variabili CSS sulla radice
 * della richiesta: il foglio `richiesta.css` li legge sempre con un ripiego, così
 * il componente regge anche senza SiteConfigProvider (test) o prima che la
 * configurazione arrivi.
 *
 * Contrasti (palette live): testo `--rq-ink-2` 0,86 → 5,9:1 su #f0ede8; bordi dei
 * controlli `--rq-line` 0,89 → 6,4:1. Mai alfa ≤ 0,65 per un testo, mai
 * `color_muted`/`color_accent` come testo piccolo.
 */
export function alpha(hex: string, a: number): string {
  const h = (hex || "").trim();
  let r = 28, g = 75, b = 59;
  const long = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(h);
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(h);
  if (long) {
    r = parseInt(long[1], 16); g = parseInt(long[2], 16); b = parseInt(long[3], 16);
  } else if (short) {
    r = parseInt(short[1] + short[1], 16); g = parseInt(short[2] + short[2], 16); b = parseInt(short[3] + short[3], 16);
  }
  return `rgba(${r},${g},${b},${a})`;
}

export function rootStyle(cfg: SiteConfig): CSSProperties {
  return {
    "--rq-ink": cfg.color_dark,
    "--rq-ink-2": alpha(cfg.color_dark, 0.86),
    "--rq-line": alpha(cfg.color_dark, 0.89),
    "--rq-rule": alpha(cfg.color_dark, 0.14),
    "--rq-addr": alpha(cfg.color_dark, 0.35),
    "--rq-tint": alpha(cfg.color_dark, 0.05),
    "--rq-surface": cfg.color_light_bg,
    "--rq-paper": cfg.color_body_bg,
    "--rq-focus": cfg.color_primary,
    "--rq-accent": cfg.color_accent,
    "--rq-muted": cfg.color_muted,
    "--rq-heading": cfg.font_heading,
    fontFamily: "inherit",
  } as CSSProperties;
}

/**
 * Altezza dell'header sticky (Header.tsx): 56 px di contenuto più 1 px di bordo
 * su mobile, 72 + 1 sopra i 767 px. La mini-cartolina della pagina si aggancia
 * sotto, e i controlli non devono finirci sotto (WCAG 2.4.11).
 */
export const HEADER_OFFSET = { mobile: 57, desktop: 73 } as const;

/** Colore decorativo della collezione dall'API: accettato solo se è un esadecimale. */
export function safeColor(v: unknown): string | null {
  return typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v) ? v : null;
}
